import { useState } from "react";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiErrorDetail } from "@/lib/api";

const PRESETS = ["25", "50", "100", "250"];

const DonationWidget = () => {
  const [amount, setAmount] = useState("50");
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { donor_name: name || null, donor_email: email || null, origin_url: window.location.origin };
      if (amount === "custom") {
        const val = parseFloat(custom);
        if (!val || val < 1) {
          toast.error("Please enter a donation amount of at least $1");
          setLoading(false);
          return;
        }
        payload.custom_amount = val;
      } else {
        payload.amount_key = amount;
      }
      const { data } = await api.post("/payments/donate", payload);
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="donation-widget" className="rounded-3xl bg-white border border-line shadow-[0_20px_40px_rgb(0,0,0,0.08)] p-6 md:p-8">
      <h3 className="font-serif text-2xl text-forest mb-5">Make a secure donation</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {PRESETS.map((p) => (
          <button
            key={p}
            data-testid={`donate-amount-${p}`}
            onClick={() => setAmount(p)}
            className={`h-14 rounded-xl border font-semibold transition-colors ${amount === p ? "bg-forest text-sand border-forest" : "border-line text-forest hover:border-forest"}`}
          >
            ${p}
          </button>
        ))}
      </div>
      <button
        data-testid="donate-amount-custom"
        onClick={() => setAmount("custom")}
        className={`mt-3 w-full h-12 rounded-xl border font-medium transition-colors ${amount === "custom" ? "bg-forest text-sand border-forest" : "border-line text-ink2 hover:border-forest"}`}
      >
        Custom amount
      </button>
      {amount === "custom" && (
        <div className="mt-3 relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-ink2">$</span>
          <Input data-testid="donate-custom-input" type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Enter amount (CAD)" className="h-12 pl-7 rounded-xl border-line" />
        </div>
      )}
      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name (optional)" className="h-12 rounded-xl border-line" data-testid="donate-name" />
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (optional)" className="h-12 rounded-xl border-line" data-testid="donate-email-input" />
      </div>
      <Button onClick={submit} disabled={loading} data-testid="donate-submit" className="mt-5 w-full h-14 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white font-semibold">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (<><Heart className="w-4 h-4 mr-2" /> Donate {amount !== "custom" ? `$${amount}` : ""} securely</>)}
      </Button>
      <p className="mt-3 text-center text-xs text-ink2">Secure checkout by Stripe · CAD · Test mode</p>
    </div>
  );
};

export default DonationWidget;
